// API service for NuPitch Incentive Matching
// Now connects to Supabase backend with intelligent matching

import { dbHelpers } from './supabase';
import { matchIncentivesToProject } from './incentiveMatching';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '/api';

// Mock data for development
const mockIncentives = [
  {
    id: '1',
    jurisdiction_name: 'United Kingdom',
    region_group: 'Europe',
    incentive_name: 'UK Film Tax Relief',
    incentive_type: 'Tax Credit',
    base_rate_percent: 25,
    estimated_savings: 2500000,
    mark_stars: 4.5,
    summary_one_liner: '25% tax credit for UK productions with cultural test',
    official_url: 'https://www.gov.uk/film-tax-relief',
    tooltip: {
      rate: '25% of UK qualifying expenditure',
      cap: 'No cap',
      min_spend: '£1M minimum UK spend',
      cultural_test: true
    }
  },
  {
    id: '2',
    jurisdiction_name: 'Georgia (US)',
    region_group: 'North America',
    incentive_name: 'Georgia Entertainment Industry Tax Credit',
    incentive_type: 'Tax Credit',
    base_rate_percent: 30,
    estimated_savings: 3000000,
    mark_stars: 4.8,
    summary_one_liner: '30% tax credit with Georgia promotional logo bonus',
    official_url: 'https://georgia.org/industries/film-entertainment',
    tooltip: {
      rate: '20% base + 10% logo bonus',
      cap: 'No cap',
      min_spend: '$500K minimum spend',
      cultural_test: false
    }
  },
  {
    id: '3',
    jurisdiction_name: 'Canada (Federal)',
    region_group: 'North America',
    incentive_name: 'Film or Video Production Services Tax Credit',
    incentive_type: 'Tax Credit',
    base_rate_percent: 16,
    estimated_savings: 1600000,
    mark_stars: 4.2,
    summary_one_liner: '16% on Canadian labour, stackable with provincial credits',
    official_url: 'https://www.canada.ca/en/canadian-heritage/services/funding/cavco-tax-credits.html',
    tooltip: {
      rate: '16% of Canadian labour',
      cap: 'No cap',
      min_spend: 'C$1M minimum budget',
      cultural_test: false
    }
  },
  {
    id: '4',
    jurisdiction_name: 'France',
    region_group: 'Europe',
    incentive_name: 'Tax Rebate for International Production (TRIP)',
    incentive_type: 'Rebate',
    base_rate_percent: 30,
    estimated_savings: 2250000,
    mark_stars: 4.0,
    summary_one_liner: '30% rebate for international productions in France',
    official_url: 'https://www.cnc.fr/web/en/funds',
    tooltip: {
      rate: '30% (40% for VFX-heavy)',
      cap: '€30M per project',
      min_spend: '€250K minimum spend',
      cultural_test: true
    }
  },
  {
    id: '5',
    jurisdiction_name: 'Australia',
    region_group: 'Asia/Oceania',
    incentive_name: 'Location Offset',
    incentive_type: 'Tax Credit',
    base_rate_percent: 30,
    estimated_savings: 2100000,
    mark_stars: 3.8,
    summary_one_liner: '30% offset for large international productions',
    official_url: 'https://www.arts.gov.au/what-we-do/screen/australian-screen-production-incentive',
    tooltip: {
      rate: '30% of qualifying expenditure',
      cap: 'No cap',
      min_spend: 'A$20M minimum spend',
      cultural_test: false
    }
  }
];

// Simulate network delay
const delay = (ms) => new Promise(resolve => setTimeout(resolve, ms));

// Extract project title from file content or form data
const extractProjectTitle = async (formData) => {
  // If user provided title, use it
  if (formData.projectTitle && formData.projectTitle.trim()) {
    return formData.projectTitle.trim();
  }
  
  // Try to extract from file content
  if (formData.file) {
    try {
      const fileContent = await readFileContent(formData.file);
      const titleMatch = extractTitleFromContent(fileContent);
      if (titleMatch) return titleMatch;
    } catch (error) {
      console.warn('Could not read file for title extraction:', error);
    }
  }
  
  // Try to extract from project text
  if (formData.projectText && formData.projectText.trim()) {
    const titleMatch = extractTitleFromContent(formData.projectText);
    if (titleMatch) return titleMatch;
  }
  
  // Generate title from other form data
  const genre = formData.genre ? formData.genre.split(',')[0].trim() : '';
  const location = formData.location ? formData.location.trim() : '';
  
  if (genre && location) {
    return `${genre.charAt(0).toUpperCase() + genre.slice(1)} Film (${location})`;
  } else if (genre) {
    return `${genre.charAt(0).toUpperCase() + genre.slice(1)} Film Project`;
  } else if (location) {
    return `Film Project (${location})`;
  }
  
  return 'Untitled Project';
};

// Read file content (basic implementation)
const readFileContent = (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => resolve(e.target.result);
    reader.onerror = reject;
    reader.readAsText(file);
  });
};

// Extract title from content using common patterns
const extractTitleFromContent = (content) => {
  if (!content) return null;
  
  const lines = content.split('\n').slice(0, 10); // Check first 10 lines
  
  // Common title patterns
  const patterns = [
    /^title:\s*(.+)$/im,
    /^"(.+)"$/m,
    /^(.+)$/m // First non-empty line as fallback
  ];
  
  for (const line of lines) {
    const trimmed = line.trim();
    if (!trimmed || trimmed.length < 3) continue;
    
    for (const pattern of patterns) {
      const match = trimmed.match(pattern);
      if (match && match[1] && match[1].length > 2 && match[1].length < 100) {
        return match[1].trim();
      }
    }
    
    // If it's a reasonable first line, use it
    if (trimmed.length > 5 && trimmed.length < 80 && !trimmed.includes('@') && !trimmed.includes('http')) {
      return trimmed;
    }
  }
  
  return null;
};

export const submitProject = async (formData) => {
  await delay(1000);
  
  try {
    // Generate proper UUID for submission ID
    const submissionId = crypto.randomUUID();
    
    // Extract or generate project title
    const projectTitle = await extractProjectTitle(formData);
    
    // Enhanced parsing based on user input and file analysis
    const parsedData = {
      working_title: projectTitle,
      production_type: formData.productionType || 'feature',
      genre: formData.genre ? formData.genre.split(',').map(g => g.trim()) : ['drama'],
      setting_countries: formData.location ? [formData.location] : ['United States'],
      setting_cities: [],
      period_years: formData.period ? { start: parseInt(formData.period), end: parseInt(formData.period) } : null,
      primary_language: formData.language || 'English',
      expected_runtime_minutes: formData.runtime || 120,
      presence_of_minors: formData.hasMinors || false,
      stunts_or_vfx_intensity: formData.vfxLevel || 'medium',
      likely_spend_split_local_percent: formData.localSpendPercent || 60
    };
    
    // Create submission object
    const submission = {
      id: submissionId,
      budget_amount: formData.budget,
      budget_currency: formData.currency || 'USD',
      parsed_metadata: parsedData,
      consent_terms: formData.consentTerms || false,
      newsletter_optin: formData.newsletter || false,
      created_at: new Date().toISOString()
    };

    try {
      // Try to save to Supabase database
      await dbHelpers.createSubmission(submission);
      console.log('✅ Submission saved to Supabase');
    } catch (dbError) {
      console.warn('⚠️ Failed to save to Supabase, using localStorage fallback:', dbError.message);
      // Fallback to localStorage if Supabase is not available
      localStorage.setItem(`submission_${submissionId}`, JSON.stringify(submission));
    }
    
    return {
      success: true,
      submissionId
    };
  } catch (error) {
    console.error('Submission error:', error);
    return {
      success: false,
      error: error.message
    };
  }
};

export const getResults = async (id, isShared = false) => {
  await delay(500);
  
  try {
    let submission;

    // Try to get submission from Supabase first
    try {
      submission = await dbHelpers.getSubmission(id);
      console.log('✅ Submission loaded from Supabase');
    } catch (dbError) {
      console.warn('⚠️ Failed to load from Supabase, trying localStorage:', dbError.message);
      // Fallback to localStorage
      const localData = localStorage.getItem(`submission_${id}`);
      if (!localData) {
        throw new Error('Submission not found');
      }
      submission = JSON.parse(localData);
    }
    
    if (!submission.id) {
      throw new Error('Submission not found');
    }

    console.log('🔍 Starting intelligent incentive matching for submission:', id);
    console.log('📊 Project data to analyze:', submission.parsed_metadata);

    // Use intelligent matching algorithm
    let matches;
    try {
      matches = await matchIncentivesToProject(submission);
      console.log(`✅ Found ${matches.length} intelligent matches`);
    } catch (matchError) {
      console.error('❌ Intelligent matching FAILED, using mock fallback');
      console.error('❌ Full error details:', matchError);
      
      // Fallback to basic mock data with some customization
      matches = [...mockIncentives].map((incentive, index) => {
        const qpe = submission.budget_amount 
          ? submission.budget_amount * (submission.parsed_metadata?.likely_spend_split_local_percent || 60) / 100
          : 5000000;
          
        const estimatedSavings = Math.min(
          qpe * (incentive.base_rate_percent / 100),
          incentive.cap || Infinity
        );
        
        return {
          ...incentive,
          rank: index + 1,
          estimatedSavings: estimatedSavings, // FIXED: Use camelCase for UI
          estimated_savings: estimatedSavings, // Keep snake_case for DB
          currency: submission.budget_currency || 'USD',
          matchScore: 50 - (index * 5), // Decreasing match scores
          matchReasoning: [`⭐ High-quality ${incentive.mark_stars}/5 star program`, `💰 ${incentive.base_rate_percent}% incentive rate`, `🎬 ${incentive.incentive_type} structure`]
        };
      });
    }

    // TEMPORARILY DISABLE match results saving to focus on display issue
    /*
    try {
      const matchResultsData = matches.map(match => ({
        submission_id: submission.id,
        incentive_id: match.id, // This might be the issue - UUID vs whatever the DB expects
        rank: match.rank,
        estimated_savings: match.estimatedSavings, // Use camelCase
        match_score: match.matchScore
      }));

      console.log('💾 Attempting to save match results:', matchResultsData.slice(0, 2));
      await dbHelpers.createMatchResults(matchResultsData);
      console.log('✅ Match results saved to Supabase');
    } catch (dbError) {
      console.warn('⚠️ Failed to save match results to Supabase:', dbError.message);
      console.warn('⚠️ This might be due to foreign key constraint - incentive IDs may not match DB schema');
    }
    */
    
    return {
      submission,
      matches,
      metadata: {
        total_matches: matches.length,
        generated_at: new Date().toISOString(),
        matching_algorithm: matches[0]?.matchScore ? 'intelligent' : 'fallback'
      }
    };
  } catch (error) {
    console.error('Get results error:', error);
    throw error;
  }
};

export const generateReport = async (submissionId, userData) => {
  await delay(2000);
  
  try {
    // In production, this would:
    // 1. Generate PDF using results data
    // 2. Upload PDF to secure storage
    // 3. Send email with download link
    // 4. Save user data to Supabase
    
    // Mock report generation
    const reportData = {
      id: Math.random().toString(36).substr(2, 9),
      submission_id: submissionId,
      user_data: userData,
      pdf_url: `https://example.com/reports/${submissionId}.pdf`,
      generated_at: new Date().toISOString()
    };
    
    // Mock email sending
    console.log('Mock email sent to:', userData.email);
    console.log('Report data:', reportData);
    
    return reportData;
  } catch (error) {
    console.error('Generate report error:', error);
    throw error;
  }
};

export const createShareLink = async (submissionId) => {
  await delay(300);
  
  try {
    const shareToken = Math.random().toString(36).substr(2, 16);
    const shareLink = {
      submission_id: submissionId,
      share_token: shareToken,
      expires_at: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString(), // 7 days
      created_at: new Date().toISOString()
    };
    
    localStorage.setItem(`share_${shareToken}`, JSON.stringify(shareLink));
    
    return {
      share_url: `${window.location.origin}/share/${shareToken}`,
      expires_at: shareLink.expires_at
    };
  } catch (error) {
    console.error('Create share link error:', error);
    throw error;
  }
};

// Mock admin functions
export const getIncentives = async () => {
  await delay(500);
  return mockIncentives;
};

export const updateIncentive = async (id, updates) => {
  await delay(300);
  // In production, this would update the incentive in Supabase
  console.log('Mock update incentive:', id, updates);
  return { success: true };
};

export const uploadIncentiveData = async (csvData) => {
  await delay(1000);
  // In production, this would parse and upload CSV data to Supabase
  console.log('Mock upload CSV data:', csvData.length, 'rows');
  return { success: true, imported: csvData.length };
};